import { Observable, BehaviorSubject, of, concatMapTo, switchMap, take, tap, filter, merge, mapTo, Subject } from 'rxjs';
import { DropdownOptionModel } from '../../Forms/models/dropdown-option.model';
import { LeftSidebarListItem, LeftSidebarSubitem } from '../models/left-sidebar-list-item.model';
import { LeftSidebarListItemButton } from '../models/left-sidebar-list-item-button.model';
import { LeftSidebarLocalizations } from '../models/left-sidebar-localizations.model';
import { AuditableFilterService } from '../../Filter/services/auditable-filter.service';
import { UserAppPreferencesModel } from '../../../Core/Users/models/user-app-preferences.model';
import { SortOrderServiceInterface } from '../../../Common/Misc/services/sort-order.service.interface';
import { SortOrderEnum } from '../../../api/shared/enums/sort-order.enum';
import { LocalizedEnum } from '../../../api/journodex/model/localized-enum';
import { FilterTypeRadioModel } from '../../Filter/models/filter-type-radio.model';
import { ListEntitiesVisibilityEnum } from '../../MonitorShared/enum/list-entities-visibility.enum';
import { DisplayGroupModel } from '../models/display-group.model';
import { DisplayGroupTypeEnum } from '../../../api/displayGroups/enums/display-group-type.enum';
import { DisplayGroupsService } from './display-groups.service';
import { UsersService } from '../../../Core/Users/services/users.service';
import { ActionsEnum } from '../../Basic/enums/actions.enum';
import { LocalizationService } from '../../../Core/Localization/services/localization.service';
import { Mode } from '../enums/mode.enum';
import { sortArray, sortArrayByString } from '../../Misc/helpers/sort/sort.helper';
import { FilterTypeCheckboxModel } from '../../Filter/models/filter-type-checkbox.model';

export abstract class LeftSidebarService extends AuditableFilterService implements SortOrderServiceInterface{

    protected quickFilterPlaceholder: string = '';
    protected quickFilterPlaceholder$: BehaviorSubject<string> = new BehaviorSubject<string>(this.quickFilterPlaceholder);

    protected hideSidebar$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    protected hideAmplifyButton$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    protected hideList$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    protected hideFilter$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    protected sortOrder: SortOrderEnum = SortOrderEnum.ascending;
    protected sortOrder$: BehaviorSubject<SortOrderEnum> = new BehaviorSubject<SortOrderEnum>(this.sortOrder);

    protected lhpSortFields$: BehaviorSubject<LocalizedEnum[]> = new BehaviorSubject<LocalizedEnum[]>([]);
    protected lhpSortFields: LocalizedEnum[];
    protected selectedSortField: LocalizedEnum = null;
    protected selectedSortField$: BehaviorSubject<LocalizedEnum> = new BehaviorSubject<LocalizedEnum>(this.selectedSortField);

    protected currentMode: Mode;
    protected modeProvider$: BehaviorSubject<Mode>;
    protected listItems: LeftSidebarListItem[];
    protected listItemsProvider: BehaviorSubject<LeftSidebarListItem[]>;
    protected listItemGroups: DisplayGroupModel[] = [];
    protected listItemGroupsProvider: BehaviorSubject<DisplayGroupModel[]>;
    protected topListItem: LeftSidebarListItem | null = null;
    protected topListItemProvider: BehaviorSubject<LeftSidebarListItem | null> = new BehaviorSubject<LeftSidebarListItem | null>(this.topListItem);
    protected newListItemId = new BehaviorSubject<number>(null);
    public newListItemId$: Observable<number> = this.newListItemId.asObservable();
    protected subitemsSubject: BehaviorSubject<number> = new BehaviorSubject<number>(null);
    subitems$: Observable<LeftSidebarSubitem[]> = of([]);
    protected selectedSubitemSubject: BehaviorSubject<LeftSidebarSubitem> = new BehaviorSubject<LeftSidebarSubitem>(null);

    protected primaryHeading: string = null;
    protected primaryHeading$: BehaviorSubject<string> = new BehaviorSubject<string>(this.primaryHeading);

    protected selectedViewMode: string;
    protected selectedViewMode$: BehaviorSubject<string> = new BehaviorSubject<string>(null);

    protected readonly sortFieldCreatedDate: LocalizedEnum = {
        enumValue: 'CREATED_DATE',
        displayString: 'Reports.sharedReports.sortFields.createdDate'
    };
    protected readonly sortFieldName: LocalizedEnum = {
        enumValue: 'NAME',
        displayString: 'Reports.sharedReports.sortFields.name'
    };
    protected readonly sortFieldStatus: LocalizedEnum = {
        enumValue: 'STATUS',
        displayString: 'Discover.sortFields.status'
    };
    protected readonly sortFieldResult: LocalizedEnum = {
        enumValue: 'RESULTS',
        displayString: 'Discover.sortFields.results'
    };

    public readonly modeToGroupTypeMap = new Map<Mode, DisplayGroupTypeEnum>([
        [ Mode.newSearch,              DisplayGroupTypeEnum.topics        ],
        [ Mode.topics,                 DisplayGroupTypeEnum.topics        ],
        [ Mode.mentions,               DisplayGroupTypeEnum.savedCoverage ],
        [ Mode.briefings,              DisplayGroupTypeEnum.briefings     ],
        [ Mode.alerts,                 DisplayGroupTypeEnum.alerts        ],
        [ Mode.mediaSubscriptions,     DisplayGroupTypeEnum.subscriptions ],
        [ Mode.discoverContacts,       DisplayGroupTypeEnum.savedSearches ],
        [ Mode.searches,               DisplayGroupTypeEnum.savedSearches ],
        [ Mode.targetingLists,         DisplayGroupTypeEnum.targetedLists ],
        [ Mode.privateContacts,        DisplayGroupTypeEnum.targetedLists ],
        [ Mode.distributions,          DisplayGroupTypeEnum.distributions ],
        [ Mode.wire,                   DisplayGroupTypeEnum.newswirePlus  ],
        [ Mode.reports,                DisplayGroupTypeEnum.reports       ],
        [ Mode.visibilityIntelligence, DisplayGroupTypeEnum.viMonitor     ]
    ]);

    /** Emitted after a mode change (one module to another), after initial filters and user prefs are applied */
    protected modeChangeCompleteSubject: Subject<void> = new Subject();
    modeChangeComplete$ = this.modeChangeCompleteSubject.asObservable();


    constructor(
        protected usersService: UsersService,
        protected displayGroupsService: DisplayGroupsService,
        protected localizationService: LocalizationService
    ) {
        super(usersService, displayGroupsService);
    }

    abstract getDropdownItems(): Observable<DropdownOptionModel[]>; // list of items to include in the dropdown
    abstract getSelectedDropdownItem(): Observable<DropdownOptionModel>; // currently selected dropdown item
    abstract selectDropdownItem(item: DropdownOptionModel): void; // When you select an item in the dropdown this gets called

    abstract getListItems(): Observable<LeftSidebarListItem[]>; // Gets currently available items in the list
    abstract getListIsComplete(): Observable<boolean>; // True if the list already has all possible items
    abstract getMoreListItems(): void; // for when you scroll down in the list, if not already complete, fetch more items
    abstract getSelectedListItem(): Observable<LeftSidebarListItem | null>; // Gets the currently selected list item
    abstract selectListItem(item: LeftSidebarListItem): void; // Selects an item from the list (eg. you clicked on it)
    abstract listItemAction(item: LeftSidebarListItem, button: LeftSidebarListItemButton, fromLHP?: boolean): void;
    abstract moveListItem(item: LeftSidebarListItem);
    abstract getReorderEnabled(): Observable<boolean>;
    abstract setListItems(items: LeftSidebarListItem[], listIsComplete?: boolean);
    abstract handleGroupDelete(ids: number[]);

    abstract isAmplifyButtonEnabled(): Observable<boolean>; // Determines if the main button should be enabled.
    abstract amplifyButtonClicked(): void; // When you click on the button at the top
    secondaryAmplifyButtonClicked(): void {};

    abstract getLocalizations(): Observable<LeftSidebarLocalizations>; // will define the map explicitly later, should be overridable by input maybe?

    protected setQuickFilterPlaceholder(placeholder: string) {
        this.quickFilterPlaceholder = placeholder;
        this.quickFilterPlaceholder$.next(this.quickFilterPlaceholder);
    }
    public getQuickFilterPlaceholder(): Observable<string> {
        return this.quickFilterPlaceholder$;
    }
    public getHideSidebar(): Observable<boolean> {
        return this.hideSidebar$;
    }
    protected toggleHideSidebar(hide: boolean) {
        this.hideSidebar$.next(hide);
    }

    public getHideAmplifyButton(): Observable<boolean> {
        return this.hideAmplifyButton$;
    }

    protected toggleHideAmplifyButton(hide: boolean): void {
        this.hideAmplifyButton$.next(hide);
    }

    protected toggleHideList(hide: boolean): void {
        this.hideList$.next(hide);
    }

    public getHideList(): Observable<boolean> {
        return this.hideList$;
    }
    public getHideFilter(): Observable<boolean> {
        return this.hideFilter$;
    }

    protected toggleHideFilter(hide: boolean): void {
        this.hideFilter$.next(hide);
    }

    public getPrimaryHeading(): Observable<string> {
        return this.primaryHeading$;
    }

    /**
     * Overridden by MonitoringLeftSidebarService for use with the show hidden items button
     * @returns A list of all currently hidden LHP list items
     */
    public getHiddenListItems(): Observable<LeftSidebarListItem[]> {
        return of([]);
    }

    /**
     * Overridden by MonitoringLeftSidebarService for use with the show hidden items button
     */
    public setVisibilityFilter() {
        return;
    }

    getQuickFilterMinLength() {
        return 3;
    }

    public getMode(): Observable<Mode> {
        return this.modeProvider$;
    }

    public getGroupedListItems(): Observable<DisplayGroupModel[]> {
        return this.listItemGroupsProvider;
    }

    public getTopListItem(): Observable<LeftSidebarListItem | null> {
        return this.topListItemProvider;
    }

    /**
     * Determines when applyFilters() is called, based on the current mode.
     * When the mode changes (entering or leaving a subfeature, e.g., Reports), we want to apply User Preference filters.
     * Any other time a routing event or sidebar list item event (CRUD action) occurs, we want to reapply the current filters,
     * whatever they may be. This allows users to override filters while within a given route and have those changes
     * persist until they leave the subfeature.
     * @param routingServiceModeChange$ An observable that emits on route change, including whether the current mode has changed
     * @param getItemsAndPrepareFilters A function returning an observable that sets and prepares filters based on the current mode
     * @param userAppPrefs An observable that will be subscribed to after getItemsAndPrepareFilters runs
     * @returns A stream that will applyFilters using user preferences on mode change, or reapply current filters not on mode change
     */
    protected createApplyFilterStream(
        routingServiceModeChange$: Observable<{ isModeChange: boolean; currentMode: number}>,
        getItemsAndPrepareFilters: (mode: number) => Observable<void>,
        userAppPrefs: Observable<UserAppPreferencesModel>
    ): Observable<null> {
        return routingServiceModeChange$.pipe(
            filter(mode => mode.isModeChange),
            switchMap(mode => {
                return this.loadUserPreferencesStream(
                    mode.currentMode,
                    getItemsAndPrepareFilters,
                    userAppPrefs
                ).pipe(
                    concatMapTo(this.reapplyFilterStream(
                        mode.currentMode,
                        routingServiceModeChange$,
                        getItemsAndPrepareFilters,
                    ))
                );
            }),
        );
    }

    /**
     * The first part of the filter application workflow. Waits for the sidebar list items to emit,
     * then waits for the user preferences filters to be set, then applies the filters and completes.
     * @param currentMode the current mode (subfeature)
     * @param getItemsAndPrepareFilters a function that returns an observable that emits whenever
     * the sidebar list items emits
     * @param userAppPrefs an observable gets the user preferences and sets the filters to be applied
     */
    private loadUserPreferencesStream(
        currentMode: number,
        getItemsAndPrepareFilters: (mode: number) => Observable<void>,
        userAppPrefs: Observable<UserAppPreferencesModel>
    ): Observable<UserAppPreferencesModel> {
        return getItemsAndPrepareFilters(currentMode).pipe(
            concatMapTo(userAppPrefs),
            tap(() => this.applyFilters()),
            // After user preferences loads, we complete the subscription, so we can switch
            // to the regular filter application stream
            take(1),
            tap(() => this.modeChangeCompleteSubject.next())
        );
    }

    /**
     * The second part of the filter application workflow. Once user preference filters have been set,
     * this stream becomes active and applies filters whenever a non-mode-change routing event occurs,
     * or when the sidebar list items emits (ie., on CRUD action)
     * @param currentMode the current mode (subfeature)
     * @param routingServiceModeChange$ an observable that emits on route change, including whether
     * the current mode has changed
     * @param getItemsAndPrepareFilters a function that returns an observable that emits whenever
     * the sidebar list items emits
     */
    private reapplyFilterStream(
        currentMode: number,
        routingServiceModeChange$: Observable<{ isModeChange: boolean; currentMode: number}>,
        getItemsAndPrepareFilters: (mode: number) => Observable<void>,
    ): Observable<null> {
        return merge(
            routingServiceModeChange$.pipe(
                filter(mode => !mode.isModeChange),
            ),
            getItemsAndPrepareFilters(currentMode)
        ).pipe(
            tap(() => this.applyFilters()),
            mapTo(null)
        );
    }


    getSortOrder(): Observable<SortOrderEnum> {
        return this.sortOrder$;
    }

    setSortOrder(order: SortOrderEnum): void {
        if (this.sortOrder !== order) {
            this.sortOrder = order;
            this.sortOrder$.next(order);
            this.applyFilters();
            this.listItemsProvider.next(this.listItems);
        }
    }


    getSortableFields(): Observable<any[]> {
        return this.lhpSortFields$;
    }

    getSortField(): Observable<any> {
        return this.selectedSortField$;
    }
    setSortField(field: any): void {
        if (field !== this.selectedSortField) {
            this.selectedSortField = field;
            this.selectedSortField$.next(field);
            this.applyFilters();
            this.listItemsProvider.next(this.listItems);
        }
    }

    protected sortList(groups: DisplayGroupModel[] = this.listItemGroups) {
        groups.forEach(group => {
            if (this.selectedSortField === this.sortFieldName) {
                sortArrayByString(group.listItems, 'label', this.sortOrder);
            } else if (this.selectedSortField === this.sortFieldCreatedDate) {
                sortArray(group.listItems, 'value', this.sortOrder);
            } else if (this.selectedSortField === this.sortFieldStatus || this.selectedSortField === this.sortFieldResult) {
                sortArray(group.listItems, 'status', this.sortOrder);
            }
        });

        this.listItemGroups = this.listItemGroups.slice();
    }

    loadSubitems(id: number): void {
        this.subitemsSubject.next(id);
    }

    selectSubitem(subitem: LeftSidebarSubitem): void {
        this.selectedSubitemSubject.next(subitem);
    }

    public getVisibilityFilterOptions<T>(visFilter: FilterTypeRadioModel<ListEntitiesVisibilityEnum>): void {
        visFilter.options = [
            {
                label: 'Monitoring.filter.visibility.all',
                value: ListEntitiesVisibilityEnum.all
            },
            {
                label: 'Monitoring.filter.visibility.visible',
                value: ListEntitiesVisibilityEnum.visible
            },
            {
                label: 'Monitoring.filter.visibility.hidden',
                value: ListEntitiesVisibilityEnum.hidden
            }
        ];
    }


    private groupFilterValues() {
        const groupFilter = this.filters.find(f => (f.id === this.displayGroupFilter.id));
        const groupfilterValues: Array<number> = groupFilter?.getValue() || [];
        const hasActiveGroupFilters = groupfilterValues.length > 0;

        return {
            hasActiveGroupFilters,
            groupfilterValues
        };
    }

    /**
     * Organizes list items into display groups and sorts.
     */
    public groupListItems(): void {
        const { hasActiveGroupFilters, groupfilterValues } = this.groupFilterValues();

        if (this.listItems.length === 0 && !hasActiveGroupFilters) { // Prevent groups from disappearing when the group filter is applied.
            this.listItemGroupsProvider.next([]);
            return;
        }

        const listGroups = this.listItemGroups.filter(group => !hasActiveGroupFilters || groupfilterValues.includes(group.id));

        listGroups.forEach(group => {
            group.listItems = this.listItems.filter(item => item.groupId === group.id);
        });

        let uncategorized = listGroups.find(group => group.type === DisplayGroupTypeEnum.uncategorized);
        if (!uncategorized) {
            uncategorized = new DisplayGroupModel();
            uncategorized.name = this.localizationService.transform('Common.lhp.groups.uncategorized');
            uncategorized.type = DisplayGroupTypeEnum.uncategorized;
            listGroups.push(uncategorized);
        }
        uncategorized.listItems = this.listItems.filter(item => !item.groupId || !listGroups.find(group => group.id === item.groupId));

        if (this.currentMode !== Mode.account && this.currentMode !== Mode.user) {
            let favorites = listGroups.find(group => group.type === DisplayGroupTypeEnum.favorites);
            if (!favorites) {
                favorites = new DisplayGroupModel();
                favorites.name = this.localizationService.transform('Common.lhp.groups.favorites');
                favorites.type = DisplayGroupTypeEnum.favorites;
                listGroups.unshift(favorites);
            }
            favorites.listItems = this.listItems.filter(item => item.isFavorite);
        }

        this.sortList(listGroups);
        this.listItemGroupsProvider.next(listGroups);
    }

    public createNewGroup() {
        this.displayGroupsService.openCreateGroupDialog();
    }

    public groupActions(group: DisplayGroupModel, action: ActionsEnum): void {
        switch(action) {
            case ActionsEnum.rename:
                this.displayGroupsService.renameGroup(group);
                break;
            case ActionsEnum.delete:
                const listItemIds = group.listItems.map(item => item.value);
                this.displayGroupsService.confirmDeleteGroup(group).subscribe((confirmed) => {
                    if (confirmed && listItemIds) {
                        this.handleGroupDelete(listItemIds);
                    }
                });
                break;
        }
    }

    /**
     * Applies a string key filter to a list of items
     * @param filter A string checkbox filter
     * @param items A list of items
     * @param getItemOptionKey A method for extracting a string key
     */
    public applyGenericStringOptions<T>(
        xFilter: FilterTypeCheckboxModel<string>,
        items: T[],
        getItemOptionKey: (item: T) => string
    ): T[] {
        const filterKeys = xFilter.getValue();

        if (!filterKeys || filterKeys.length < 1) {
            return items;
        }
        return items.filter((item) => {
            return filterKeys.includes(getItemOptionKey(item));
        });
    }
}
